import React, { useState, useEffect, useRef } from 'react';
import { PropTypes } from 'prop-types';
import Editor from '@draft-js-plugins/editor';
import createLinkPlugin from '@draft-js-plugins/anchor';
import createInlineToolbarPlugin from '@draft-js-plugins/inline-toolbar';
import { ItalicButton, BoldButton, UnderlineButton } from '@draft-js-plugins/buttons';
import { EditorState, RichUtils } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { stateFromHTML } from 'draft-js-import-html';

// import '!css-loader!@draft-js-plugins/inline-toolbar/lib/plugin.css';

// Here's your chance to pass in a configuration object (see below).
const linkPlugin = createLinkPlugin();

// Pass the `linkPlugin.LinkButton` into the structure of the inline toolbar.
// const inlineToolbarPlugin = createInlineToolbarPlugin({
//   structure: [BoldButton, ItalicButton, UnderlineButton, linkPlugin.LinkButton],
// });

// const { InlineToolbar } = inlineToolbarPlugin;
// const plugins = [inlineToolbarPlugin, linkPlugin];

function MyEditor({
  retrieveInput, lang, value, classes, retrieveOnBlur, categoryFromSection,
}) {
  const editor = useRef(null);
  // const [editorState, setEditorState] = useState(createEditorStateWithText(text));
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [heightState, setHeightState] = useState(16);
  const [isFirstLoad, setFirstLoad] = useState(true);

  useEffect(() => {
    if (value && isFirstLoad) {
      const contentState = stateFromHTML(value);
      setEditorState(EditorState.createWithContent(contentState));
      setFirstLoad(false);
    }
  }, [value]);

  const [{ plugins, InlineToolbar }] = useState(() => {
    const inlineToolbarPlugin = createInlineToolbarPlugin();
    const { InlineToolbar } = inlineToolbarPlugin;
    const plugins = [inlineToolbarPlugin, linkPlugin];
    return {
      plugins,
      InlineToolbar,
    };
  });

  const getLinkColorFromCategory = (cat) => {
    if (cat) {
      const colors = {
        tickets: '#4dc0b5',
        entradas: '#4dc0b5',
        barcelona: '#67A0CE',
        pro: '#67A0CE',
        'primavera-pro': '#67A0CE',
        'nits-del-forum': '#7730C9',
        'pro-online': '#DA642C',
        madrid: '#DA44BE',
        artist: '#FF0000',
        news: '#AF1651',
        oporto: '#7DD6AD',
        porto: '#7DD6AD',
        concerts: '#FFAF52',
        tours: '#FFAF52',
        concert: '#FFAF52',
        club: '#DA44BE',
        radio: '#FF7BA0',
        la: '#FFE65B',
        los_angeles: '#FFE65B',
        'los-angeles': '#FFE65B',
        london: '#EA725F',
        benidorm: '#6AD2FF',
        weekender: '#6AD2FF',
      };
      return colors.hasOwnProperty(cat) ? colors[cat] : '';
    }
  };

  const handleChange = (editorInfo) => {
    setEditorState(editorInfo);
    const contentState = editorInfo.getCurrentContent();
    const stateToHtmlOptions = {
      entityStyleFn: (entity) => {
        const entityType = entity.get('type').toLowerCase();
        if (entityType === 'link') {
          const linkColor = getLinkColorFromCategory(categoryFromSection);
          const data = entity.getData();
          return {
            element: 'a',
            attributes: {
              href: data.url,
              target: '_blank',
            },
            style: {
              color: linkColor || '',
              textDecoration: 'underline',
            },
          };
        }
      },
    };
    const contentHtml = stateToHTML(contentState, stateToHtmlOptions);
    const contentHtmlWithEmptyTextFields = contentHtml === '<p><br></p>' ? '' : contentHtml;

    retrieveInput(contentHtmlWithEmptyTextFields, lang);

    // handle view height
    const textLength = contentHtml.length;
    const numberOfP = (contentHtml.match(/<p>/g) || []).length;
    let multiplier = 1;
    if (numberOfP > 25 || textLength > 6000) {
      multiplier = 6;
      setHeightState(16 * multiplier);
    } else if (numberOfP > 20 || textLength > 4800) {
      multiplier = 5;
      setHeightState(16 * multiplier);
    } else if (numberOfP > 15 || textLength > 3600) {
      multiplier = 4;
      setHeightState(16 * multiplier);
    } else if (numberOfP > 10 || textLength > 2400) {
      multiplier = 3;
      setHeightState(16 * multiplier);
    } else if (numberOfP > 5 || textLength > 1200) {
      multiplier = 2;
      setHeightState(16 * multiplier);
    } else {
      multiplier = 1;
      setHeightState(16 * multiplier);
    }
    setHeightState(16 * multiplier);
  };

  const handleKeyCommand = (command, editorState1) => {
    const newState = RichUtils.handleKeyCommand(editorState1, command);
    if (newState) {
      setEditorState(newState);
      handleChange(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  const onBoldClick = () => {
    const newState = RichUtils.toggleInlineStyle(editorState, 'BOLD');
    setEditorState(newState);
    handleChange(newState);
  };
  const onItalicClick = () => {
    const newState = RichUtils.toggleInlineStyle(editorState, 'ITALIC');
    setEditorState(newState);
    handleChange(newState);
  };
  const onUnderlineClick = () => {
    const newState = RichUtils.toggleInlineStyle(editorState, 'UNDERLINE');
    setEditorState(newState);
    handleChange(newState);
  };

  const blur = () => {
    if (retrieveOnBlur !== null) {
      retrieveOnBlur(true);
    }
  };

  const focus = () => {
    editor.current.focus();
    if (retrieveOnBlur !== null) {
      retrieveOnBlur(false);
    }
  };

  return (
    <div>
      <div className={`absolute pin-t pin-r ${classes} w-auto`}>
        <div className="flex justify-between items-center text-base w-full">
          <button type="button" onClick={onBoldClick} className="mx-2">
            <span className="font-americaMonoBold">B</span>
          </button>
          <button type="button" onClick={onItalicClick} className="mx-2">
            <span className="italic font-americaMonoMedium">I</span>
          </button>
          <button type="button" onClick={onUnderlineClick} className="mx-2">
            <span className="underline font-americaMonoMedium">U</span>
          </button>
          {/* <button type="button" onClick={onUnderlineClick} className="mx-2">
            <span className="font-icon icon-add-link text-sm m-0 p-0" />
          </button> */}
        </div>
      </div>
      <div className={`h-${heightState}`} onClick={focus} onBlur={blur}>
        <Editor editorState={editorState} onChange={handleChange} placeholder="Input text please" handleKeyCommand={handleKeyCommand} plugins={plugins} ref={editor} />
        <InlineToolbar>
          {externalProps => (
            <>
              <BoldButton {...externalProps} />
              <ItalicButton {...externalProps} />
              <UnderlineButton {...externalProps} />
              <linkPlugin.LinkButton {...externalProps} />
            </>
          )}
        </InlineToolbar>
      </div>
    </div>
  );
}

MyEditor.propTypes = {
  retrieveInput: PropTypes.func.isRequired,
  lang: PropTypes.string.isRequired,
  value: PropTypes.string,
  classes: PropTypes.string,
  retrieveOnBlur: PropTypes.func,
  categoryFromSection: PropTypes.string,
};

MyEditor.defaultProps = {
  value: '',
  classes: '',
  retrieveOnBlur: null,
  categoryFromSection: '',
};

export default MyEditor;
